import { useRef, useCallback } from 'react';

/**
 * Backdrop-dismiss handlers for modal/drawer overlays.
 *
 * Plain `onClick={e => e.target === e.currentTarget && onClose()}` is buggy:
 * `click` fires on the common ancestor of mousedown/mouseup targets, so a
 * text-selection drag that STARTS inside the panel and releases over the
 * backdrop retargets to the backdrop and closes the dialog, losing user input.
 * Dismissal here is anchored to where the press started — both mousedown and
 * click must land on the backdrop itself. A short grace period after mount
 * also swallows stray clicks from the opening gesture (e.g. a double-click on
 * the trigger button landing on the freshly mounted backdrop).
 *
 * Usage: <div className="overlay" {...useBackdropDismiss(onClose)}>
 */
export default function useBackdropDismiss(onClose, { openGraceMs = 300 } = {}) {
  const pressStartedOnBackdrop = useRef(false);
  const mountedAt = useRef(Date.now());

  const onMouseDown = useCallback((e) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback((e) => {
    const anchored = pressStartedOnBackdrop.current;
    pressStartedOnBackdrop.current = false;
    if (!anchored || e.target !== e.currentTarget) return;
    if (Date.now() - mountedAt.current < openGraceMs) return;
    onClose?.();
  }, [onClose, openGraceMs]);

  return { onMouseDown, onClick };
}
